import React from "react";
import { Lock, AlertCircle, RotateCw } from "lucide-react";
import UserAvatar from "components/UserAvatar";
import RelativeTime from "components/RelativeTime";

// Resolve a mention entry (userId string, or a {userId,name,email} object) to a
// display name via the internal-user directory.
function mentionName(mv, directory = {}) {
  if (mv && typeof mv === "object") {
    return mv.name || mv.displayName || mv.email || mv.userId || "user";
  }
  const u = directory[mv];
  if (!u) return mv;
  return (
    u.name || u.displayName || [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || mv
  );
}

// Render a message body with each mentioned user's `@<name>` highlighted inline
// (Slack/GitHub style) — no detached chip. We match the exact `@<displayName>`
// tokens the composer inserts; display names can contain spaces, so we match the
// literal token strings rather than splitting on whitespace. Tokens are matched
// longest-first so `@First Last` wins over a bare `@First`, and a trailing
// `(?!\w)` guard avoids highlighting a name that is a prefix of a longer one
// (e.g. `@Ann` inside `@Anna`). A mention whose token isn't present in the body
// is simply skipped — no fallback badge.
function renderBody(body, mentions, directory) {
  if (!body || !mentions || mentions.length === 0) return body;
  const tokens = Array.from(
    new Set(
      mentions
        .map((mv) => mentionName(mv, directory))
        .filter(Boolean)
        .map((n) => `@${n}`)
    )
  ).sort((a, b) => b.length - a.length);
  if (tokens.length === 0) return body;
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})(?!\\w)`, "g");
  return body.split(re).map((part, i) =>
    tokens.includes(part) ? (
      <span key={i} className="font-medium text-info underline">
        {part}
      </span>
    ) : (
      part
    )
  );
}

/**
 * ConversationThread (SOUND-493)
 * -------------------------------------------------------
 * The reading spine of the ticket workspace. Messages sorted `createdAt` asc.
 *
 *   - Public messages render bare on the page (avatar + text + hairline) so the
 *     thread reads like a conversation, not a stack of cards.
 *   - Internal notes are unmistakable: an amber `border-l-warning` tinted block
 *     with a 🔒 Internal badge. This is the single most important safety
 *     affordance in the thread — an internal note must never read as client-
 *     visible. (The API already strips internal messages for non-internal
 *     callers, so clients never receive them.)
 *
 * Optimistic bubbles (`_optimistic`) render dimmed; failed sends (`_error`)
 * surface a "Not sent" tag + Retry.
 */
function authorLabel(author = {}, currentUserId, isInternalViewer) {
  if (author.userId && author.userId === currentUserId) return "You";
  const name = author.displayName || author.email || "Unknown";
  // Client / partner viewers see internal authors as "· Sound team".
  if (!isInternalViewer) return `${name} · Sound team`;
  return name;
}

export default function ConversationThread({
  messages = [],
  currentUserId,
  isInternalViewer = false,
  onRetry,
  mentionDirectory = {},
}) {
  if (!messages.length) return null;

  return (
    <div className="flex flex-col">
      {messages.map((m) => {
        const isInternalNote = m.visibility === "internal";
        const name = authorLabel(m.author, currentUserId, isInternalViewer);
        const avatarUser = {
          name: m.author?.displayName,
          email: m.author?.email,
        };

        if (isInternalNote) {
          return (
            <div
              key={m.messageId}
              className={`my-1.5 flex gap-3 rounded-r-lg border-l-2 border-l-warning bg-warning/[0.06] px-3.5 py-3 ${
                m._optimistic ? "opacity-60" : "animate-fade-in"
              }`}
            >
              <div className="shrink-0">
                <UserAvatar user={avatarUser} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{name}</span>
                  <span className="text-xs text-muted-foreground">
                    <RelativeTime value={m.createdAt} />
                  </span>
                  <span className="badge badge-warning">
                    <Lock className="h-3 w-3" />
                    Internal
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-snug text-foreground/90">
                  {renderBody(m.body, m.mentions, mentionDirectory)}
                </p>
                {m._error && (
                  <RetryLine onRetry={() => onRetry?.(m)} />
                )}
              </div>
            </div>
          );
        }

        return (
          <div
            key={m.messageId}
            className={`flex gap-3 border-b border-border/70 py-3 last:border-b-0 ${
              m._optimistic ? "opacity-60" : "animate-fade-in"
            }`}
          >
            <div className="shrink-0">
              <UserAvatar user={avatarUser} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{name}</span>
                <span className="text-xs text-muted-foreground">
                  <RelativeTime value={m.createdAt} />
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-snug text-foreground/90">
                {renderBody(m.body, m.mentions, mentionDirectory)}
              </p>
              {m._error && <RetryLine onRetry={() => onRetry?.(m)} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RetryLine({ onRetry }) {
  return (
    <div className="mt-1.5 flex items-center gap-2 text-xs text-destructive">
      <AlertCircle className="h-3.5 w-3.5" />
      <span>Not sent.</span>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:no-underline"
      >
        <RotateCw className="h-3 w-3" />
        Retry
      </button>
    </div>
  );
}
